import { useState } from "react";
import {
  Plus,
  Check,
  Pencil,
  X,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import { Money } from "./Money";

type AdditionalCost = {
  id: string;
  po_id: string;
  cost_type: string;
  label: string | null;
  amount: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

import {
  type CostType,
  COST_TYPE_OPTIONS,
  getCostTypeLabel,
  getCostTypeIcon,
  getCostTypeColor,
} from "../utils/costTypeConfig";

type DraftRow = {
  costType: CostType;
  label: string;
  amount: string;
};

const emptyDraft = (): DraftRow => ({ costType: "freight", label: "", amount: "" });

interface AdditionalCostsSectionProps {
  costs: AdditionalCost[];
  orderId: string;
  currency: string;
  /** BLU-373 PR2b: PO exchange rate so the read-mode cost rows flip with the toggle */
  exchangeRate: number | null;
  isEditable: boolean;
  onMutationSuccess: () => void;
}

export function AdditionalCostsSection({
  costs,
  orderId,
  currency,
  exchangeRate,
  isEditable,
  onMutationSuccess,
}: AdditionalCostsSectionProps) {
  const [addingNew, setAddingNew] = useState(false);
  const [draft, setDraft] = useState<DraftRow>(emptyDraft());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftRow>(emptyDraft());
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const upsertMutation = trpc.purchaseOrders.upsertAdditionalCost.useMutation({
    onSuccess: () => {
      onMutationSuccess();
      setAddingNew(false);
      setDraft(emptyDraft());
      setEditingId(null);
      toast({ title: "Additional cost saved" });
    },
    onError: (err) => {
      toast({ title: "Failed to save additional cost", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = trpc.purchaseOrders.deleteAdditionalCost.useMutation({
    onSuccess: () => {
      onMutationSuccess();
      toast({ title: "Additional cost removed" });
    },
    onError: (err) => {
      toast({ title: "Failed to remove additional cost", description: err.message, variant: "destructive" });
    },
  });

  const handleSaveNew = () => {
    const amount = parseFloat(draft.amount);
    if (isNaN(amount)) return;
    if (draft.costType === "other" && !draft.label.trim()) return;
    upsertMutation.mutate({
      orderId,
      costType: draft.costType,
      label: draft.costType === "other" ? draft.label || null : null,
      amount,
    });
  };

  const handleStartEdit = (cost: AdditionalCost) => {
    setEditingId(cost.id);
    setEditDraft({
      costType: cost.cost_type as CostType,
      label: cost.label ?? "",
      amount: String(cost.amount),
    });
  };

  const handleSaveEdit = (costId: string) => {
    const amount = parseFloat(editDraft.amount);
    if (isNaN(amount)) return;
    if (editDraft.costType === "other" && !editDraft.label.trim()) return;
    upsertMutation.mutate({
      orderId,
      costId,
      costType: editDraft.costType,
      label: editDraft.costType === "other" ? editDraft.label || null : null,
      amount,
    });
  };

  const handleDelete = (costId: string) => {
    deleteMutation.mutate({ orderId, costId });
    setDeleteConfirmId(null);
  };

  const currencySymbol = new Intl.NumberFormat("en", {
    style: "currency",
    currency: currency || "AUD",
    currencyDisplay: "narrowSymbol",
  })
    .formatToParts(0)
    .find((p) => p.type === "currency")?.value ?? "$";

  // Nothing to show in read-only mode with no costs
  if (!isEditable && costs.length === 0) return null;

  return (
    <div className="mt-3 space-y-1.5">
      {/* Existing cost rows */}
      {costs.map((cost) => {
        const Icon = getCostTypeIcon(cost.cost_type);

        if (editingId === cost.id && isEditable) {
          return (
            <div key={cost.id} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-muted/50 rounded-md p-2">
              <Select
                value={editDraft.costType}
                onValueChange={(v) => setEditDraft({ ...editDraft, costType: v as CostType })}
              >
                <SelectTrigger className="w-full sm:w-[140px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COST_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {editDraft.costType === "other" && (
                <Input
                  value={editDraft.label}
                  onChange={(e) => setEditDraft({ ...editDraft, label: e.target.value })}
                  placeholder="Description"
                  className="h-8 text-sm flex-1 sm:max-w-[180px]"
                  maxLength={200}
                />
              )}

              <div className="flex items-center gap-2 ml-auto">
                <div className="relative w-[120px]">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {currencySymbol}
                  </span>
                  <Input
                    inputMode="decimal"
                    value={editDraft.amount}
                    onChange={(e) => setEditDraft({ ...editDraft, amount: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(cost.id); }}
                    placeholder="0.00"
                    className="h-8 text-sm pl-8 text-right"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                  onClick={() => handleSaveEdit(cost.id)}
                  disabled={upsertMutation.isPending}
                  aria-label="Save"
                >
                  {upsertMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                  onClick={() => setEditingId(null)}
                  aria-label="Cancel"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        }

        // Read-only row
        return (
          <div key={cost.id} className="flex items-center justify-between text-sm group">
            <span className="text-foreground flex items-center gap-1.5 min-w-0">
              <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${getCostTypeColor(cost.cost_type)}`} />
              <span className="truncate">
                {cost.cost_type === "other" && cost.label ? cost.label : getCostTypeLabel(cost.cost_type)}
              </span>
              {isEditable && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                    onClick={() => handleStartEdit(cost)}
                    aria-label="Edit cost"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-500"
                    onClick={() => setDeleteConfirmId(cost.id)}
                    disabled={deleteMutation.isPending}
                    aria-label="Remove cost"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </>
              )}
            </span>
            <span className="flex-shrink-0">
              <Money amount={cost.amount} poCurrency={currency} exchangeRate={exchangeRate} />
            </span>
          </div>
        );
      })}

      {/* Add new cost row */}
      {addingNew && isEditable && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-muted/50 rounded-md p-2">
          <Select
            value={draft.costType}
            onValueChange={(v) => setDraft({ ...draft, costType: v as CostType })}
          >
            <SelectTrigger className="w-full sm:w-[140px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COST_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {draft.costType === "other" && (
            <Input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Description"
              className="h-8 text-sm flex-1 sm:max-w-[180px]"
              maxLength={200}
            />
          )}

          <div className="flex items-center gap-2 ml-auto">
            <div className="relative w-[120px]">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                {currencySymbol}
              </span>
              <Input
                inputMode="decimal"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveNew(); }}
                placeholder="0.00"
                className="h-8 text-sm pl-8 text-right"
                autoFocus
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
              onClick={handleSaveNew}
              disabled={upsertMutation.isPending || !draft.amount || (draft.costType === "other" && !draft.label.trim())}
              aria-label="Save"
            >
              {upsertMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => { setAddingNew(false); setDraft(emptyDraft()); }}
              aria-label="Cancel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add button */}
      {isEditable && !addingNew && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-sm text-muted-foreground hover:text-foreground gap-1.5 border-dashed"
          onClick={() => setAddingNew(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add additional cost
        </Button>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteConfirmId !== null} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove additional cost?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this cost from the purchase order and update the total.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
